import React, { useEffect } from 'react';
import { useQuery } from '/src/api';
import {
    useModalsContext,
    useStoreConfigContext,
} from '/src/providers';
import {
    Modal,
    Form,
    useForm,
    Loader,
    Errors,
    FORM_VALIDATORS,
} from '/src/components';
import graphQLQueries from './graph-ql-queries';

const CustomerAddress = ({
    address: {
        firstname = '',
        lastname = '',
        street = [''],
        city = '',
        postcode = '',
        country_code = '',
        region: {
            region_code,
            region,
        } = {},
        telephone = '',
        company = '',
        default_shipping,
        default_billing,
    } = {},
    setAddress,
    title,
}) => {
    const { translate } = useStoreConfigContext();
    const { hideModal } = useModalsContext();
    const {
        data: dataCountries,
        isLoading: isLoadingCountries,
        runQuery: countriesRunQuery,
        setValues,
    } = useQuery({
        data: [],
        query: graphQLQueries.countries,
        queryKey: ['countries'],
    });
    const {
        data,
        errors,
        setErrors,
        fieldsErrors,
        handleChange,
        validateData
    } = useForm(
        {
            firstname,
            lastname,
            street: street[0],
            city,
            postcode,
            country_code,
            region:  '',
            telephone,
            company,
            default_shipping,
            default_billing,
        },
        {
            firstname: { isRequired: true },
            lastname: { isRequired: true },
            street: { isRequired: true },
            city: { isRequired: true },
            postcode: {
                isRequired: true,
                validator: FORM_VALIDATORS.maxPostCode,
            },
            telephone: { isRequired: true },
            country_code: { isRequired: true },
        }
    );
    const isLoading = isLoadingCountries;

    const checkShippingAddress = () => {
        const available = dataCountries.filter(({ available_for_shipping }) => available_for_shipping).map(({ id }) => id);

        return available.includes(data.country_code);
    };

    const checkBillingAddress = () => {
        const available = dataCountries.filter(({ available_for_billing }) => available_for_billing).map(({ id }) => id);

        return available.includes(data.country_code);
    };

    const getAvailableRegions = () => {
        const { available_regions } = dataCountries.find(({ id }) => id === data.country_code) || {};

        return available_regions;
    };

    const handleSubmit = e => {
        e.preventDefault();
        const isValidData = validateData();

        if (isValidData) {
            setValues({ isLoading: true });
            const { region, ...address } = data;
            const selectedRegion = {};
            const availableRegions = getAvailableRegions();

            if (!!availableRegions) {
                const { id, code, name } = availableRegions.find(({ code }) => code === region);
                selectedRegion.region = name;
                selectedRegion.region_id = id;
                selectedRegion.region_code = code;
            } else {
                selectedRegion.region = region;
                selectedRegion.region_id = 0;
                selectedRegion.region_code = region;
            }

            setAddress({ ...address, region: selectedRegion })
                .then(() => hideModal())
                .catch(errors => {
                    setValues({ isLoading: false });
                    setErrors({ errors });
                });
        }
    };

    const renderRegions = () => {
        const availableRegions = getAvailableRegions();

        if (!!availableRegions) {
            return (
                <Form.Select
                    label={translate('Region')}
                    name="region"
                    onChange={handleChange}
                    value={data.region}
                >
                    {
                        availableRegions.map(({ code, name }) => (
                            <Form.Option key={code} value={code}>
                                {name}
                            </Form.Option>
                        ))
                    }
                </Form.Select>
            )
        }

        return (
            <Form.Input
                label={translate('Region')}
                type="text"
                name="region"
                value={data.region}
                onChange={handleChange}
            />
        )
    };

    useEffect(() => {
        if (data.country_code && !!dataCountries.length) {
            const availableRegions = getAvailableRegions();
            let currentRegion = !!availableRegions ? region_code : region;

            if (data.country_code !== country_code) {
                currentRegion = !!availableRegions ? availableRegions[0].code : '';
            }

            handleChange({
                target: {
                    value: currentRegion,
                    name: 'region'
                }
            });
        }
    }, [data.country_code, dataCountries]);

    useEffect(() => {
        if (dataCountries.length && !data.country_code) {
            handleChange({
                target: {
                    value: dataCountries[0].id,
                    name: 'country_code'
                }
            });
        }
    }, [dataCountries]);

    useEffect(() => {
        countriesRunQuery();
    }, []);

    return (
        <Modal { ...(!isLoading ? { toggle: hideModal } : {}) }>
            <Loader isLoading={isLoading}>
                <div>{title}</div>
                <Errors errors={errors} translate={translate} />
                <Form onSubmit={handleSubmit}>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('First name')}
                            type="text"
                            name="firstname"
                            value={data.firstname}
                            onChange={handleChange}
                            error={translate(fieldsErrors.firstname)}
                        />
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('Last name')}
                            type="text"
                            name="lastname"
                            value={data.lastname}
                            onChange={handleChange}
                            error={translate(fieldsErrors.lastname)}
                        />
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('Street')}
                            type="text"
                            name="street"
                            value={data.street}
                            onChange={handleChange}
                            error={translate(fieldsErrors.street)}
                        />
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('City')}
                            type="text"
                            name="city"
                            value={data.city}
                            onChange={handleChange}
                            error={translate(fieldsErrors.city)}
                        />
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('Postcode')}
                            type="text"
                            name="postcode"
                            value={data.postcode}
                            onChange={handleChange}
                            error={translate(fieldsErrors.postcode)}
                        />
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Select
                            label={translate('Countries')}
                            name="country_code"
                            onChange={handleChange}
                            value={data.country_code}
                        >
                            {
                                <optgroup label={translate('Delivery address')}>
                                    {
                                        dataCountries?.filter(({ available_for_shipping }) => available_for_shipping).map(({ id, full_name_locale }) => (
                                            <Form.Option key={id} value={id}>
                                                {full_name_locale}
                                            </Form.Option>
                                        ))
                                    }
                                </optgroup>
                            }
                            {
                                <optgroup label={translate('Billing address')}>
                                    {
                                        dataCountries?.filter(({ available_for_billing }) => available_for_billing).map(({ id, full_name_locale }) => (
                                            <Form.Option key={id} value={id}>
                                                {full_name_locale}
                                            </Form.Option>
                                        ))
                                    }
                                </optgroup>
                            }
                        </Form.Select>
                    </Form.Column>
                    <Form.Column mb>
                        {renderRegions()}
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('Telephone')}
                            type="tel"
                            name="telephone"
                            value={data.telephone}
                            onChange={handleChange}
                            error={translate(fieldsErrors.telephone)}
                        />
                    </Form.Column>
                    <Form.Column mb>
                        <Form.Input
                            label={translate('Company')}
                            name="company"
                            value={data.company}
                            onChange={handleChange}
                            error={translate(fieldsErrors.company)}
                        />
                    </Form.Column>
                    {
                        checkShippingAddress() &&
                        <Form.Column mb>
                            <Form.Checkbox
                                label={translate('Default shipping')}
                                name="default_shipping"
                                checked={data.default_shipping}
                                onChange={handleChange}
                            />
                        </Form.Column>
                    }
                    {
                        checkBillingAddress() &&
                        <Form.Column>
                            <Form.Checkbox
                                label={translate('Default billing')}
                                name="default_billing"
                                checked={data.default_billing}
                                onChange={handleChange}
                            />
                        </Form.Column>
                    }
                    <Form.Column mt>
                        <Form.Button disabled={isLoading}>
                            {translate('Submit')}
                        </Form.Button>
                    </Form.Column>
                </Form>
            </Loader>
        </Modal>
    );
};

export default CustomerAddress;
